import { planTeam, type TeamPlan } from './plan';
import { actPlayer, makeBotMemory, type BotMemory } from './actPlayer';
import { actKeeper } from './keeper';
import { difficultyProfile, pickSide, DEFAULT_DIFFICULTY, type DifficultyLevel, type DifficultySide } from './difficulty';
import { NULL_INPUT } from '../sim/input';
import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from '../sim/events';
import type { PlayerInput, EntityId, TeamIdx } from '../types';

// BotDriver (§8, §10.1) — server-side, produces PlayerInputs for every entity NOT held by a human this
// tick. The AI is pure and lives in @fafi/shared; the driver owns the mutable per-bot memory + a seeded AI
// rng (kept OUT of the sim's MatchState.rng, §3 determinism) and a per-team plan cache refreshed every
// PLAN_PERIOD ticks. Sent-off entities get no input (NULL_INPUT); the plan redistributes over the rest.

export class BotDriver {
  private readonly aiRng: { rng: number };
  private readonly memory = new Map<EntityId, BotMemory>();
  private readonly planCache = new Map<TeamIdx, { tick: number; plan: TeamPlan; dir: 1 | -1 }>();

  // D46 Items 3+4: the match difficulty (1..5, default 3). Opposite scaling — each team's curve is picked
  // by human presence (ally if it has a human, else enemy), so it's derived per plan refresh, not stored.
  constructor(private readonly cfg: SimConfig, aiSeed: number, private readonly difficulty: DifficultyLevel = DEFAULT_DIFFICULTY) {
    this.aiRng = { rng: aiSeed >>> 0 };
  }

  private mem(id: EntityId): BotMemory {
    let m = this.memory.get(id);
    if (!m) {
      m = makeBotMemory();
      this.memory.set(id, m);
    }
    return m;
  }

  private planFor(state: MatchState, team: TeamIdx, tick: number, humanHeld: ReadonlySet<EntityId>): TeamPlan {
    const cached = this.planCache.get(team);
    // D62: the cache is keyed on the ATTACK DIRECTION as well as the tick window. formationTargets are
    // baked from state.attackDir at plan time, and endHalf flips it mid-tick with no notification — so
    // for the rest of the window every off-ball bot chased the mirrored OLD shape while the carrier
    // brain (which reads attackDir live) already attacked the new goal.
    if (cached && cached.dir === state.attackDir[team] && tick - cached.tick < this.cfg.ai.PLAN_PERIOD) return cached.plan;
    // D42: the AI layer (NOT the sim) knows which teammates are human — bots play FOR them
    const humans = new Set<EntityId>();
    let otherHuman = false;
    for (const h of humanHeld) {
      const t = state.players[h]?.team;
      if (t === team) humans.add(h);
      else if (t !== undefined) otherHuman = true;
    }
    // D85: use the ALLY curve ONLY when this team is being helped against an ALL-BOT opponent. In PvP
    // (humans on BOTH sides) BOTH teams use the ENEMY curve — one global bot-IQ dial that scales UP with
    // the level (dumb → IDENTITY(3) → brutal), so raising the difficulty makes ALL bots smarter and the
    // match stays symmetric. Human-vs-bots keeps opposite scaling. Level 3 = IDENTITY on both ⇒ equal teams.
    const side: DifficultySide = pickSide(humans.size > 0, otherHuman);
    const plan = planTeam(state, team, this.cfg, humans, difficultyProfile(this.difficulty, side));
    this.planCache.set(team, { tick, plan, dir: state.attackDir[team] });
    return plan;
  }

  /**
   * Feed a tick's events so victims hold a grudge against their fouler/puncher (§8.1 retaliation) — the
   * missing wiring that makes the retaliate evaluator fire in a real match. Call after each step().
   */
  observeEvents(events: readonly SimEvent[], tick: number): void {
    for (const e of events) {
      if (e.t === 'FOUL' && e.offender !== e.victim) this.mem(e.victim).lastAggressor = { id: e.offender, tick };
      else if (e.t === 'PUNCH_LANDED') this.mem(e.victim).lastAggressor = { id: e.puncher, tick };
    }
  }

  /** Inputs for every bot entity (id ∉ humanHeld). Ascending id order ⇒ deterministic rng draw order. */
  inputsFor(state: MatchState, humanHeld: ReadonlySet<EntityId>, tick: number): Map<EntityId, PlayerInput> {
    const out = new Map<EntityId, PlayerInput>();
    for (const p of state.players) {
      if (humanHeld.has(p.id)) {
        // D62: FORGET this entity's bot memory while a human drives it. actPlayerCore is the only place
        // that clears mem.kick, re-arms tackleReadyTick and runs the possession-spell bookkeeping, and
        // it does not run for a human-held entity — so a kick latched on the last bot tick survived the
        // whole human spell and was re-pressed, with a stale aim and zero movement, the moment the bot
        // got the entity back (tackleReadyTick is an ABSOLUTE tick, so a stale one skips the D32
        // reaction window entirely).
        this.memory.delete(p.id);
        continue; // a human is driving this entity
      }
      if (p.sentOff) {
        out.set(p.id, NULL_INPUT); // no input for a sent-off player (T-AI-011)
        continue;
      }
      const plan = this.planFor(state, p.team, tick, humanHeld);
      const inp = p.isGk
        ? actKeeper(state, p.id, this.mem(p.id), this.aiRng, this.cfg, plan.humans, plan.profile) // D44: honors "pass to me"; D46: difficulty
        : actPlayer(state, p.id, plan, this.mem(p.id), this.aiRng, this.cfg);
      out.set(p.id, inp);
    }
    return out;
  }
}
